import React, { FocusEvent, useRef, useState } from "react";
import { TextInputBox } from "./textInputBox";
import { OptionDropDown, DropdownOption } from "./optionDropDown";

interface TextInputBoxListProps {
  id: string;
  inputValueList: string[];
  onInputChange: React.Dispatch<React.SetStateAction<string[]>>;
  onFocusChange?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeHolder?: string;
  dropDownOptions?: DropdownOption[];
  maxLength?: number;
  className?: string;
  error?: boolean;
}

interface TextInputBoxItemProps {
  id: string;
  index: number;
  value: string;
  placeHolder?: string;
  maxLength: number;
  error: boolean;
  dropDownOptions?: DropdownOption[];
  focusedInputBox: string;
  showRemoveButton: boolean;
  onValueChange: (index: number, value: string) => void;
  onValueRemove: (index: number) => void;
  onFocusChange: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

const TextInputBoxItem: React.FC<TextInputBoxItemProps> = ({
  id,
  index,
  value,
  placeHolder,
  maxLength,
  error,
  dropDownOptions,
  focusedInputBox,
  showRemoveButton,
  onValueChange,
  onValueRemove,
  onFocusChange
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className="relative">
      <div className="flex items-center gap-3">
        <div className="flex-1 relative" ref={containerRef}>
          <TextInputBox
            id={`${id}-${index}`}
            inputValue={value}
            onInputChange={(value) => onValueChange(index, value)}
            onFocusChange={onFocusChange}
            placeHolder={placeHolder}
            maxLength={maxLength}
            error={error}
          />
          {dropDownOptions && (
            <OptionDropDown
              value={value}
              onChange={(value) => onValueChange(index, value)}
              isOpen={focusedInputBox === `${id}-${index}`}
              options={dropDownOptions}
              triggerRef={containerRef}
            />
          )}
        </div>

        {showRemoveButton && (
          <button
            type="button"
            onClick={() => onValueRemove(index)}
            className="flex-shrink-0 w-9 h-9 flex items-center justify-center text-error-600 bg-error-50 hover:bg-error-100 rounded-lg border border-error-200 hover:border-error-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-error-500 focus:ring-offset-2 group"
            aria-label="Remove item"
          >
            <svg
              className="w-4 h-4 group-hover:scale-110 transition-transform duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

const TextInputBoxList: React.FC<TextInputBoxListProps> = ({
  id,
  inputValueList,
  onInputChange,
  onFocusChange,
  placeHolder,
  dropDownOptions,
  maxLength = 52,
  className = "",
  error = false
}) => {
  const [focusedInputBox, setFocusedInputBox] = useState<string>("");

  const handleValueChange = (index: number, value: string) => {
    const updatedValues = [...inputValueList];
    updatedValues[index] = value.trim();
    onInputChange(updatedValues);
  };

  const handleValueRemove = (index: number) => {
    if (inputValueList.length > 1) {
      const updatedValues = inputValueList.filter((_, idx) => idx !== index);
      onInputChange(updatedValues);
    }
  };

  const handleFocusChange = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocusedInputBox(focusedInputBox === e.target.id ? "" : e.target.id);
    onFocusChange?.(e);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {inputValueList.map((value, index) => (
        <TextInputBoxItem
          key={index}
          id={id}
          index={index}
          value={value}
          placeHolder={placeHolder}
          maxLength={maxLength}
          error={error}
          dropDownOptions={dropDownOptions}
          focusedInputBox={focusedInputBox}
          showRemoveButton={inputValueList.length > 1}
          onValueChange={handleValueChange}
          onValueRemove={handleValueRemove}
          onFocusChange={handleFocusChange}
        />
      ))}
    </div>
  );
};

export { TextInputBoxList };
